import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Plus, MessageCircle, Loader2, Pencil, Trash2, Send, ChevronDown, ChevronRight } from 'lucide-react';
import { apiUrl } from '../../utils/apiBase';
import type { UserSession } from '../../types';

interface WhatsappTemplate {
  id: string;
  nome: string;
  conteudo: string;
  destinatarios: string[];
  ativo: boolean;
  created_at?: string;
  updated_at?: string;
}

interface FormData {
  nome: string;
  conteudo: string;
  destinatarios: string[];
  ativo: boolean;
}

// Componente Modal que renderiza no topo da página
const Modal: React.FC<{ children: React.ReactNode; isOpen: boolean }> = ({ children, isOpen }) => {
  if (!isOpen) return null;
  
  const modalElement = document.getElementById('root') || document.body;
  
  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[99999] p-4 overflow-auto"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 999999,
        margin: 0,
        paddingTop: '30px'
      }}
    >
      <div style={{ width: '88%', maxWidth: '88vw', minHeight: '83vh' }}>
        {children}
      </div>
    </div>,
    modalElement
  );
};

interface FluxoData {
  id: string;
  name: string;
}

interface EtapaData {
  id: string;
  nome: string;
}

interface CampoData {
  id: string;
  nome: string;
  descricao?: string;
  tipo: string;
  obrigatorio?: boolean;
}

interface LoadingState {
  isLoading: boolean;
  text: string;
  subtext: string;
}

interface PreviewData {
  conteudo: string;
  destinatarios: string[];
}

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const Toggle: React.FC<ToggleProps> = ({ checked, onChange, size = 'md', label }) => {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex items-center rounded-full transition-colors ${
          checked ? 'bg-green-600' : 'bg-gray-300'
        } ${
          size === 'sm' ? 'h-5 w-9' : size === 'lg' ? 'h-7 w-12' : 'h-6 w-11'
        }`}
      >
        <span
          className={`inline-block transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-1'
          } ${
            size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'
          }`}
        />
      </button>
      {label && <span className="text-sm text-gray-700">{label}</span>}
    </div>
  );
};

interface WhatsappTemplatesProps {
  session: UserSession | null;
}

const WhatsappTemplates: React.FC<WhatsappTemplatesProps> = ({ session }) => {
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsappTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    conteudo: '',
    destinatarios: [''],
    ativo: true
  });
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    text: '',
    subtext: ''
  });
  const [error, setError] = useState<string>('');
  const [previewMode, setPreviewMode] = useState(false);

  // Estados para campos dinâmicos
  const [fluxos, setFluxos] = useState<FluxoData[]>([]);
  const [etapas, setEtapas] = useState<EtapaData[]>([]);
  const [campos, setCampos] = useState<CampoData[]>([]);
  const [selectedFluxo, setSelectedFluxo] = useState<string | null>(null);
  const [selectedEtapa, setSelectedEtapa] = useState<string | null>(null);
  
  // Estados para preview
  const [previewData, setPreviewData] = useState<PreviewData>({
    conteudo: '',
    destinatarios: []
  });

  // Ref para o textarea de conteúdo
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Carregar templates
  const carregarTemplates = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      const url = empresaId
        ? apiUrl(`/templates-whatsapp?empresaId=${empresaId}`)
        : apiUrl('/templates-whatsapp');
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('Dados recebidos dos templates:', data);
        
        let templatesArray = [];
        
        // Verificar se é um array ou objeto com propriedade data/templates
        if (Array.isArray(data)) {
          templatesArray = data;
        } else if (data && Array.isArray(data.data)) {
          templatesArray = data.data;
        } else if (data && Array.isArray(data.templates)) {
          templatesArray = data.templates;
        } else {
          console.warn('❌ API retornou dados vazios ou inválidos:', data);
          setTemplates([]);
          return;
        }
        
        // Normalizar o ID para garantir compatibilidade
        const templatesNormalizados = templatesArray.map((template: any) => ({
          ...template,
          id: template.id || template._id // Usar _id como fallback se id não existir
        }));
        
        console.log('Templates normalizados:', templatesNormalizados);
        setTemplates(templatesNormalizados);
      } else {
        console.error('Erro na resposta da API:', response.status);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setTemplates([]);
    }
  };

  // Carregar fluxos
  const carregarFluxos = async () => {
    try {
      if (!session) {
        console.log('Nenhuma sessão ativa, não carregando fluxos');
        return;
      }

      // Usar o endpoint correto de flows do MongoDB
      const empresaId = session.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      console.log('Carregando fluxos para templates de WhatsApp:', { 
        usuario: session.usuario.nome, 
        empresaId,
        url 
      });
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('Dados brutos recebidos:', data);
      
      // Aceitar tanto array puro quanto objeto com {success,data}
      let flows = data;
      if (data?.data && Array.isArray(data.data)) flows = data.data;
      if (data?.flows && Array.isArray(data.flows)) flows = data.flows;
      
      console.log('Fluxos processados:', flows);
      
      if (flows && Array.isArray(flows)) {
        console.log('Fluxos recebidos do MongoDB:', flows.length);
        console.log('Primeiro fluxo:', flows[0]);
        
        const fluxosFormatados = flows.map((f: any) => ({
          id: f.id || f._id,
          name: f.title || f.name || f.nome || 'Sem nome'
        }));
        
        console.log('Fluxos formatados:', fluxosFormatados);
        setFluxos(fluxosFormatados);
      } else {
        console.error('Erro ao carregar fluxos: resposta vazia ou inválida', flows);
      }
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  // Carregar etapas do fluxo
  const carregarEtapas = async (fluxoId: string) => {
    try {
      console.log('Carregando etapas para fluxo:', fluxoId);
      
      // Buscar o fluxo específico do MongoDB (mesmo endpoint)
      const empresaId = session?.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('Dados recebidos:', data);
      
      let flows = data;
      if (data?.data && Array.isArray(data.data)) flows = data.data;
      if (data?.flows && Array.isArray(data.flows)) flows = data.flows;
      
      console.log('Fluxos processados:', flows);
      
      if (Array.isArray(flows)) {
        // Buscar por ID ou nome do fluxo
        const flow = flows.find((f: any) => {
          const flowId = f.id || f._id;
          const flowName = f.title || f.name || f.nome;
          console.log('Comparando fluxo:', { flowId, flowName, buscando: fluxoId });
          return flowId === fluxoId || flowName === fluxoId;
        });
        
        console.log('Fluxo encontrado:', flow);
        
        if (flow) {
          const stages = flow.stages || flow.etapas || flow.steps || [];
          console.log('Etapas encontradas:', stages);
          
          const etapasFormatadas = stages.map((stage: any, index: number) => ({
            id: stage.id || stage._id || `stage-${index}`,
            nome: stage.title || stage.nome || stage.name || `Etapa ${index + 1}`
          }));
          
          console.log('Etapas formatadas:', etapasFormatadas);
          setEtapas(etapasFormatadas);
        } else {
          console.log('Fluxo não encontrado');
          setEtapas([]);
        }
      } else {
        console.log('Dados não são um array');
        setEtapas([]);
      }
    } catch (error) {
      console.error('Erro ao carregar etapas:', error);
      setEtapas([]);
    }
  };

  // Carregar campos da etapa
  const carregarCampos = async (etapaId: string) => {
    try {
      console.log('Carregando campos para etapa:', etapaId, 'do fluxo:', selectedFluxo);
      
      if (!selectedFluxo) {
        console.log('Nenhum fluxo selecionado');
        return;
      }
      
      // Buscar o fluxo específico do MongoDB
      const empresaId = session?.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('Dados recebidos para campos:', data);
      
      let flows = data;
      if (data?.data && Array.isArray(data.data)) flows = data.data;
      if (data?.flows && Array.isArray(data.flows)) flows = data.flows;
      
      console.log('Fluxos processados para campos:', flows);
      
      if (Array.isArray(flows)) {
        // Buscar fluxo por ID ou nome
        const flow = flows.find((f: any) => {
          const flowId = f.id || f._id;
          const flowName = f.title || f.name || f.nome;
          return flowId === selectedFluxo || flowName === selectedFluxo;
        });
        
        console.log('Fluxo encontrado para campos:', flow);
        
        if (flow) {
          const stages = flow.stages || flow.etapas || flow.steps || [];
          console.log('Etapas do fluxo:', stages);
          
          // Buscar etapa por ID ou nome
          const stage = stages.find((s: any) => {
            const stageId = s.id || s._id;
            const stageName = s.title || s.nome || s.name;
            console.log('Comparando etapa:', { stageId, stageName, buscando: etapaId });
            return stageId === etapaId || stageName === etapaId;
          });
          
          console.log('Etapa encontrada:', stage);
          
          if (stage) {
            const campos = stage.campos || stage.fields || stage.form || [];
            console.log('Campos brutos encontrados:', campos);
            
            const camposFormatados = campos.map((campo: any) => ({
              id: campo.id || campo._id || Math.random().toString(),
              nome: campo.nome || campo.name || campo.label || 'Campo sem nome',
              descricao: campo.descricao || campo.description || '',
              tipo: campo.tipo || campo.type || 'texto',
              obrigatorio: campo.obrigatorio || campo.required || false
            }));
            
            console.log('Campos formatados:', camposFormatados);
            setCampos(camposFormatados);
          } else {
            console.log('Etapa não encontrada');
            setCampos([]);
          }
        } else {
          console.log('Fluxo não encontrado para carregar campos');
          setCampos([]);
        }
      } else {
        console.log('Dados não são um array ao carregar campos');
        setCampos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar campos:', error);
      setCampos([]);
    }
  };

  const showLoading = (_type: string, text: string, subtext: string) => {
    setLoading({
      isLoading: true,
      text,
      subtext
    });
  };

  const hideLoading = () => {
    setLoading({
      isLoading: false,
      text: '',
      subtext: ''
    });
  };

  useEffect(() => {
    if (session?.empresa?.id || session?.usuario?.empresaId) {
      carregarTemplates();
      carregarFluxos();
    }
  }, [session?.empresa?.id, session?.usuario?.empresaId]);

  const salvarTemplate = async () => {
    if (!formData.nome.trim() || !formData.conteudo.trim()) {
      setError('Nome e conteúdo são obrigatórios');
      return;
    }

    // Filtrar destinatários vazios
    const destinatariosLimpos = formData.destinatarios.filter(d => d.trim() !== '');
    if (destinatariosLimpos.length === 0) {
      setError('Adicione pelo menos um número de telefone');
      return;
    }

    showLoading('save', 'Salvando template', 'Configurando template de WhatsApp...');

    try {
      const templateData = {
        ...formData,
        destinatarios: destinatariosLimpos
      };

      const url = editingTemplate 
        ? apiUrl(`/templates-whatsapp/${editingTemplate.id}`)
        : apiUrl('/templates-whatsapp');

      console.log('🔄 Salvando template:', {
        editingTemplate: !!editingTemplate,
        templateId: editingTemplate?.id,
        url,
        method: editingTemplate ? 'PUT' : 'POST',
        data: templateData
      });

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify(templateData)
      });

      console.log('📡 Resposta do servidor:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ Template salvo com sucesso:', responseData);
        await carregarTemplates();
        fecharModal();
        setError('');
      } else {
        const errorData = await response.json();
        console.error('❌ Erro na resposta:', errorData);
        setError(errorData.message || errorData.error || 'Erro ao salvar template');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar template:', error);
      setError('Erro ao salvar template: ' + (error?.message || 'Erro desconhecido'));
    } finally {
      hideLoading();
    }
  };

  const excluirTemplate = async (template: WhatsappTemplate) => {
    if (!window.confirm(`Tem certeza que deseja excluir o template "${template.nome}"?`)) {
      return;
    }

    showLoading('delete', 'Excluindo template', 'Removendo template...');

    try {
      const response = await fetch(apiUrl(`/templates-whatsapp/${template.id}`), {
        method: 'DELETE',
        headers: {
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        }
      });

      if (response.ok) {
        await carregarTemplates();
      } else {
        setError('Erro ao excluir template');
      }
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      setError('Erro ao excluir template');
    } finally {
      hideLoading();
    }
  };

  const toggleTemplateAtivo = async (templateId: string, ativo: boolean) => {
    try {
      const response = await fetch(apiUrl(`/templates-whatsapp/${templateId}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify({ ativo })
      });

      if (response.ok) {
        await carregarTemplates();
      } else {
        setError('Erro ao atualizar template');
      }
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      setError('Erro ao atualizar template');
    }
  };

  const toggleAllTemplates = async (ativo: boolean) => {
    showLoading('toggle', ativo ? 'Ativando templates' : 'Pausando templates', 'Atualizando todos os templates...');

    try {
      await Promise.all((Array.isArray(templates) ? templates : []).map(template => 
        fetch(apiUrl(`/templates-whatsapp/${template.id}`), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Data': JSON.stringify({
              email: session?.usuario?.email,
              role: session?.usuario?.role,
              empresaId: session?.empresa?.id || session?.usuario?.empresaId
            })
          },
          body: JSON.stringify({ ativo })
        })
      ));
      
      await carregarTemplates();
    } catch (error) {
      console.error('Erro ao atualizar templates:', error);
      setError('Erro ao atualizar templates');
    } finally {
      hideLoading();
    }
  };

  const enviarTeste = async (templateId: string) => {
    const template = Array.isArray(templates) ? templates.find(t => t.id === templateId) : null;
    if (!template) return;

    // Verificar se há destinatários configurados
    const destinatariosValidos = template.destinatarios?.filter((d: string) => d && d.trim()) || [];
    if (destinatariosValidos.length === 0) {
      setError('Este template não possui destinatários configurados');
      return;
    }

    showLoading('send', 'Enviando teste', 'Enviando mensagem de teste via WhatsApp...');

    try {
      const response = await fetch(apiUrl('/enviar-whatsapp-template'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: templateId,
          // Dados de exemplo para substituir variáveis no teste
          field_values: {
            'nome': 'Teste',
            'Nome': 'Teste',
            'card.titulo': 'Card de Teste',
            'card.descricao': 'Descrição de teste',
            'Data de Vencimento': new Date().toLocaleDateString('pt-BR'),
            'hora': new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            'numero': '12345',
            'data': new Date().toLocaleDateString('pt-BR'),
            'valor': 'R$ 150,00',
            'empresa': 'Empresa Teste',
          }
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(`✅ Teste enviado com sucesso!\n${data.enviados || 1} mensagem(ns) enviada(s).`);
      } else {
        const errorMsg = data.error || data.message || 'Erro ao enviar teste';
        setError(errorMsg);
        console.error('Erro no envio:', data);
      }
    } catch (error) {
      console.error('Erro ao enviar teste:', error);
      setError('Erro ao enviar teste: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      hideLoading();
    }
  };

  const gerarPreview = () => {
    // Substituir variáveis por dados de exemplo
    let conteudoPreview = formData.conteudo;
    
    // Exemplos de dados
    const exemplosDados: { [key: string]: string } = {
      'nome': 'João Silva',
      'numero': '12345',
      'data': new Date().toLocaleDateString('pt-BR'),
      'valor': 'R$ 150,00',
      'empresa': 'Minha Empresa',
      'telefone': '+55 48 99999-9999'
    };

    // Substituir todas as variáveis encontradas
    conteudoPreview = conteudoPreview.replace(/\{\{([^}]+)\}\}/g, (_match, variavel) => {
      const nomeVariavel = variavel.trim();
      return exemplosDados[nomeVariavel] || `[${nomeVariavel}]`;
    });

    setPreviewData({
      conteudo: conteudoPreview,
      destinatarios: formData.destinatarios.filter(d => d.trim())
    });
    
    setPreviewMode(true);
  };

  const abrirModal = (template?: WhatsappTemplate) => {
    if (template) {
      console.log('🔄 Abrindo modal para edição:', {
        template,
        id: template.id,
        keys: Object.keys(template)
      });
      setEditingTemplate(template);
      setFormData({
        nome: template.nome,
        conteudo: template.conteudo,
        destinatarios: template.destinatarios.length > 0 ? template.destinatarios : [''],
        ativo: template.ativo
      });
    } else {
      console.log('🆕 Abrindo modal para criar novo template');
      setEditingTemplate(null);
      setFormData({
        nome: '',
        conteudo: '',
        destinatarios: [''],
        ativo: true
      });
    }
    setShowModal(true);
    setPreviewMode(false);
    // Reset seletores
    setSelectedFluxo(null);
    setSelectedEtapa(null);
    setCampos([]);
    setEtapas([]);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setPreviewMode(false);
    setSelectedFluxo(null);
    setSelectedEtapa(null);
    setCampos([]);
    setEtapas([]);
  };

  const adicionarCampo = () => {
    setFormData(prev => ({
      ...prev,
      destinatarios: [...prev.destinatarios, '']
    }));
  };

  const removerCampo = (index: number) => {
    setFormData(prev => ({
      ...prev,
      destinatarios: prev.destinatarios.filter((_, i) => i !== index)
    }));
  };

  const atualizarCampo = (index: number, valor: string) => {
    setFormData(prev => ({
      ...prev,
      destinatarios: prev.destinatarios.map((item, i) => i === index ? valor : item)
    }));
  };

  // Função para inserir variável no conteúdo
  const inserirVariavel = (nomeCampo: string) => {
    const variavel = `{{${nomeCampo}}}`;
    
    setFormData(prev => ({
      ...prev,
      conteudo: prev.conteudo + variavel
    }));
  };

  // Handlers para drag and drop
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, nomeCampo: string) => {
    e.dataTransfer.setData('text/plain', nomeCampo);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const nomeCampo = e.dataTransfer.getData('text/plain');
    if (nomeCampo) {
      inserirVariavel(nomeCampo);
    }
  };

  // Verificação mais robusta para administrador
  const userRole = session?.usuario?.role as any;
  const isAdmin = userRole === 'administrator' || 
                  userRole === 'admin' || 
                  (userRole as string)?.toLowerCase() === 'administrator' ||
                  (userRole as string)?.toLowerCase() === 'administrador';

  console.log('🔐 Verificação de admin DETALHADA:', {
    userRole: userRole,
    roleType: typeof userRole,
    roleOriginal: session?.usuario?.role,
    isAdmin,
    userEmail: session?.usuario?.email,
    userNome: session?.usuario?.nome,
    sessionDataCompleta: session?.usuario,
    sessionCompleta: session,
    localStorageSession: localStorage.getItem('session'),
    localStorageUsers: localStorage.getItem('users'),
    roleComparisons: {
      equals_administrator: userRole === 'administrator',
      equals_admin: userRole === 'admin',
      toLowerCase_administrator: (userRole as string)?.toLowerCase() === 'administrator',
      toLowerCase_administrador: (userRole as string)?.toLowerCase() === 'administrador'
    }
  });

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6">
      {/* Loading Overlay */}
      {loading.isLoading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl border border-gray-200 text-center max-w-md">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{loading.text}</h3>
            <p className="text-gray-600">{loading.subtext}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-2 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="p-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-green-600" />
                  </div>
                  Templates de WhatsApp Business
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Configure templates e automações de WhatsApp Business
                </p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={Array.isArray(templates) && templates.some(t => t.ativo)}
                    onChange={() => toggleAllTemplates(!Array.isArray(templates) || !templates.some(t => t.ativo))}
                    size="md"
                    label={Array.isArray(templates) && templates.some(t => t.ativo) ? 'Pausar todos' : 'Ativar todos'}
                  />
                  <button
                    onClick={() => abrirModal()}
                    className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 text-sm shadow-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Novo Template
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div>
            {!Array.isArray(templates) || templates.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <div className="mx-auto w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-4">
                  <MessageCircle className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Nenhum template configurado</h3>
                <p className="text-sm text-gray-500">
                  Comece criando seu primeiro template de WhatsApp
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {Array.isArray(templates) && templates.map((template) => (
                  <div key={template.id} className="bg-white rounded-xl border border-gray-200 hover:border-green-200 hover:shadow-sm transition-all overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-sm">{template.nome}</h3>
                            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium ${template.ativo ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${template.ativo ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                              {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                          <p className="text-gray-600 text-xs mb-2 line-clamp-2">{template.conteudo}</p>
                          <p className="text-[11px] text-gray-400 flex items-center gap-1">
                            <span>📱</span>
                            Destinatários: {Array.isArray(template.destinatarios) ? template.destinatarios.join(', ') : '—'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => enviarTeste(template.id)}
                          className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                          title="Enviar Teste"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => abrirModal(template)}
                              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => toggleTemplateAtivo(template.id, !template.ativo)}
                              className={`p-1.5 rounded-lg transition-colors ${template.ativo ? 'text-gray-400 hover:text-orange-600 hover:bg-orange-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                              title={template.ativo ? 'Pausar' : 'Ativar'}
                            >
                              {template.ativo ? '⏸️' : '▶️'}
                            </button>
                            <button
                              onClick={() => excluirTemplate(template)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal de Criação/Edição */}
          {showModal && (
            <div 
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 overflow-hidden"
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
            >
              <div className="bg-white rounded-xl w-full max-w-[98vw] h-[98vh] flex flex-col shadow-2xl mx-auto my-auto border border-gray-200">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                  <h2 className="text-lg font-bold text-gray-900">
                    {editingTemplate ? 'Editar Template WhatsApp' : 'Novo Template WhatsApp'}
                  </h2>
                  <div className="flex gap-3">
                    <button
                      onClick={gerarPreview}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Preview
                    </button>
                    <button
                      onClick={fecharModal}
                      className="text-gray-500 hover:text-gray-700 p-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden">
                  <div className="h-full grid grid-cols-1 lg:grid-cols-3 gap-3 p-3">
                    {/* Formulário */}
                    <div className="lg:col-span-2 overflow-y-auto space-y-4 h-full">
                      {!previewMode ? (
                        <div className="space-y-4">
                          {/* Nome */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Nome do Template</label>
                            <input
                              type="text"
                              value={formData.nome}
                              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                              placeholder="Nome do template"
                            />
                          </div>

                          {/* Conteúdo */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Conteúdo da Mensagem</label>
                            <textarea
                              ref={contentTextareaRef}
                              value={formData.conteudo}
                              onChange={(e) => setFormData(prev => ({ ...prev, conteudo: e.target.value }))}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              className="w-full border border-gray-200 rounded-xl px-4 py-3 h-80 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm resize-none"
                              placeholder="Olá {{nome}}! 📱 Seu pedido {{numero}} está pronto para retirada. Use {{variáveis}} para dados dinâmicos dos fluxos."
                            />
                          </div>

                          {/* Destinatários */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Números de Telefone</label>
                            <div className="max-h-72 overflow-y-auto space-y-3">
                              {formData.destinatarios.map((dest, index) => (
                                <div key={index} className="flex gap-2">
                                  <input
                                    type="tel"
                                    value={dest}
                                    onChange={(e) => atualizarCampo(index, e.target.value)}
                                    className="flex-1 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                                    placeholder="+5548999654321 ou {{telefone}} (com código do país)"
                                  />
                                  <button
                                    onClick={() => removerCampo(index)}
                                    className="text-red-500 hover:text-red-700 px-3 py-1 hover:bg-red-50 rounded-xl transition-colors"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={adicionarCampo}
                              className="text-green-600 hover:text-green-800 text-sm font-medium py-2 px-4 rounded-xl hover:bg-green-50 transition-colors mt-3"
                            >
                              + Adicionar Número
                            </button>
                          </div>

                          {/* Botões */}
                          <div className="flex gap-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                            <button
                              onClick={salvarTemplate}
                              disabled={loading.isLoading}
                              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition-all transform hover:scale-[1.02] font-medium"
                            >
                              {loading.isLoading ? 'Salvando...' : 'Salvar Template'}
                            </button>
                            <button
                              onClick={fecharModal}
                              className="bg-gray-100 text-gray-700 px-8 py-3 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h3 className="font-medium mb-4">Preview do WhatsApp</h3>
                          <div className="border rounded-lg p-4 bg-gray-50">
                            <div className="bg-green-100 rounded-lg p-3 max-w-xs">
                              <div className="whitespace-pre-wrap text-sm">{previewData.conteudo}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Sidebar - Campos dos Fluxos */}
                    <div className="bg-gray-50/50 rounded-xl p-4 overflow-y-auto h-full">
                      {!previewMode ? (
                        <div>
                          <h3 className="font-medium mb-4">Campos Dinâmicos</h3>
                          
                          {/* Seletor de Fluxo */}
                          <div className="mb-4">
                            <label className="block text-sm text-gray-700 mb-2">Fluxo</label>
                            <select
                              value={selectedFluxo || ''}
                              onChange={(e) => {
                                const fluxoId = e.target.value;
                                setSelectedFluxo(fluxoId);
                                if (fluxoId) {
                                  carregarEtapas(fluxoId);
                                } else {
                                  setEtapas([]);
                                  setCampos([]);
                                  setSelectedEtapa(null);
                                }
                              }}
                              className="w-full border rounded px-3 py-2 text-sm"
                            >
                              <option value="">Selecione um fluxo...</option>
                              {fluxos.map((fluxo) => (
                                <option key={fluxo.id} value={fluxo.id}>{fluxo.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Seletor de Etapa */}
                          {selectedFluxo && (
                            <div className="mb-4">
                              <label className="block text-sm text-gray-700 mb-2">Etapa</label>
                              <select
                                value={selectedEtapa || ''}
                                onChange={(e) => {
                                  const etapaId = e.target.value;
                                  setSelectedEtapa(etapaId);
                                  if (etapaId) {
                                    carregarCampos(etapaId);
                                  } else {
                                    setCampos([]);
                                  }
                                }}
                                className="w-full border rounded px-3 py-2 text-sm"
                              >
                                <option value="">Selecione uma etapa...</option>
                                {etapas.map((etapa) => (
                                  <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Lista de Campos */}
                          {selectedFluxo && selectedEtapa && (
                            <div>
                              <label className="block text-sm text-gray-700 mb-2">Campos disponíveis</label>
                              <div className="space-y-2 max-h-60 overflow-y-auto">
                                {campos.map((campo) => (
                                  <div
                                    key={campo.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, campo.nome)}
                                    onClick={() => inserirVariavel(campo.nome)}
                                    className="p-3 border rounded-lg bg-white hover:bg-blue-50 cursor-pointer transition-colors group"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <div className="font-medium text-sm">{campo.descricao || campo.nome}</div>
                                        <div className="text-xs text-gray-500">
                                          Variável: {`{{${campo.nome}}}`} • Tipo: {campo.tipo}
                                        </div>
                                        {campo.obrigatorio && (
                                          <div className="text-xs text-green-600">Obrigatório</div>
                                        )}
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          inserirVariavel(campo.nome);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 text-green-600 hover:text-green-800 text-xs px-2 py-1 rounded transition-all"
                                        title={`Inserir no conteúdo: {{${campo.nome}}}`}
                                      >
                                        📱
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              <div className="mt-3 p-3 bg-green-50 rounded text-sm">
                                <p className="font-medium mb-1">💡 Como usar:</p>
                                <p>• <strong>Clique</strong> no campo para inserir no conteúdo</p>
                                <p>• <strong>Arraste</strong> o campo para o conteúdo</p>
                              </div>
                            </div>
                          )}

                          {/* Estado vazio */}
                          {!selectedFluxo && (
                            <div className="p-4 text-center text-gray-500">
                              <ChevronDown size={32} className="mx-auto mb-2 text-gray-300" />
                              <p>Selecione um fluxo para ver os campos disponíveis</p>
                            </div>
                          )}

                          {selectedFluxo && !selectedEtapa && (
                            <div className="p-4 text-center text-gray-500">
                              <ChevronRight size={32} className="mx-auto mb-2 text-gray-300" />
                              <p>Selecione uma etapa para ver os campos</p>
                            </div>
                          )}

                          {selectedFluxo && selectedEtapa && campos.length === 0 && (
                            <div className="p-4 text-center text-gray-500">
                              <p>Esta etapa não possui campos configurados</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <h3 className="font-medium mb-4">Informações do Template</h3>
                          <div className="space-y-2 text-sm">
                            <p><strong>Nome:</strong> {formData.nome}</p>
                            <p><strong>Destinatários:</strong> {formData.destinatarios.filter(d => d.trim()).length}</p>
                            <p><strong>Status:</strong> {formData.ativo ? 'Ativo' : 'Inativo'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criação/Edição usando Portal */}
      <Modal isOpen={showModal}>
        <div className="bg-white rounded-2xl w-full h-full flex flex-col shadow-2xl">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              {editingTemplate ? 'Editar Template WhatsApp' : 'Novo Template WhatsApp'}
            </h2>
            <div className="flex gap-3">
              <button
                onClick={gerarPreview}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Preview
              </button>
              <button
                onClick={fecharModal}
                className="text-gray-500 hover:text-gray-700 p-2 text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              {/* Lado Esquerdo - Formulário */}
              <div className="p-6 overflow-y-auto border-r border-gray-200">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Template
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Digite o nome do template"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.ativo}
                          onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                          className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${formData.ativo ? 'bg-green-600' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.ativo ? 'translate-x-6' : 'translate-x-1'}`} />
                        </div>
                        <span className="ml-3 text-sm text-gray-700">
                          {formData.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destinatários (um por linha)
                    </label>
                    <textarea
                      value={formData.destinatarios.join('\n')}
                      onChange={(e) => setFormData({
                        ...formData, 
                        destinatarios: e.target.value.split('\n')
                      })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      rows={4}
                      placeholder="Digite os números dos destinatários (um por linha)&#10;Ex: 5511999999999"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conteúdo do Template
                    </label>
                    <textarea
                      value={formData.conteudo}
                      onChange={(e) => setFormData({...formData, conteudo: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
                      rows={12}
                      placeholder="Digite o conteúdo do template..."
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={salvarTemplate}
                      className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      {editingTemplate ? 'Atualizar Template' : 'Criar Template'}
                    </button>
                    <button
                      onClick={fecharModal}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>

              {/* Lado Direito - Campos dos Fluxos e Informações */}
              <div className="p-6 overflow-y-auto bg-gray-50">
                <div className="space-y-6">
                  {/* Seção de Campos Disponíveis dos Fluxos */}
                  <div>
                    <h3 className="font-medium mb-4 text-gray-700">Campos Disponíveis dos Fluxos</h3>
                    
                    {/* 1. Selecione o Fluxo */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        1. Selecione o Fluxo
                      </label>
                      <select
                        value={selectedFluxo || ''}
                        onChange={(e) => {
                          const fluxoId = e.target.value;
                          setSelectedFluxo(fluxoId);
                          setSelectedEtapa(null);
                          setCampos([]);
                          setEtapas([]);
                          if (fluxoId) {
                            carregarEtapas(fluxoId);
                          }
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      >
                        <option value="">Selecione um fluxo...</option>
                        {fluxos.map((fluxo) => (
                          <option key={fluxo.id} value={fluxo.id}>
                            {fluxo.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 2. Selecione a Etapa */}
                    {selectedFluxo && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-600 mb-2">
                          2. Selecione a Etapa
                        </label>
                        <select
                          value={selectedEtapa || ''}
                          onChange={(e) => {
                            const etapaId = e.target.value;
                            setSelectedEtapa(etapaId);
                            setCampos([]);
                            if (etapaId) {
                              carregarCampos(etapaId);
                            }
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        >
                          <option value="">Selecione uma etapa...</option>
                          {etapas.map((etapa) => (
                            <option key={etapa.id} value={etapa.id}>
                              {etapa.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* 3. Campos da Etapa */}
                    {selectedFluxo && selectedEtapa && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-600 mb-2">
                          3. Campos da Etapa
                        </label>
                        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                          {campos.length > 0 ? (
                            campos.map((campo, index) => (
                              <div
                                key={index}
                                className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                                onClick={() => inserirVariavel(campo.nome)}
                                title={`Clique para inserir {{${campo.nome}}} no conteúdo`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-medium text-sm text-gray-700">
                                      {campo.nome}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Variável: {`{{${campo.nome}}}`} • Tipo: {campo.tipo}
                                      {campo.obrigatorio && <span className="ml-1 text-red-500">Obrigatório</span>}
                                    </div>
                                  </div>
                                  <div className="text-xs text-blue-600 hover:text-blue-800">
                                    📋
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              Esta etapa não possui campos configurados
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Como usar */}
                    {selectedFluxo && selectedEtapa && campos.length > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-xs text-blue-800 font-medium mb-1">💡 Como usar:</div>
                        <div className="text-xs text-blue-700">
                          • <strong>Clique</strong> no campo para inserir no conteúdo<br/>
                          • <strong>Arraste</strong> o campo para o assunto ou conteúdo
                        </div>
                      </div>
                    )}

                    {!selectedFluxo && (
                      <div className="text-center text-gray-500 text-sm py-8">
                        Selecione um fluxo para ver os campos disponíveis
                      </div>
                    )}
                  </div>

                  {/* Seção de Informações do Template */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-medium mb-4">Informações do Template</h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Nome:</strong> {formData.nome || 'Não informado'}</p>
                      <p><strong>Destinatários:</strong> {formData.destinatarios.filter(d => d.trim()).length}</p>
                      <p><strong>Status:</strong> {formData.ativo ? 'Ativo' : 'Inativo'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WhatsappTemplates;
